import { useState, useEffect, useRef } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function StockDashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const dashboardRef = useRef<HTMLDivElement>(null);

  // Formatage en GNF
  const formatGNF = (value: number): string => {
    if (value === 0) return '0 GNF';
    return value.toLocaleString('fr-GN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + ' GNF';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [summaryRes, categoryRes, lowStockRes, outOfStockRes, movementsRes] = await Promise.all([
          getResources('stock/summary'),
          getResources('stock/by-category'),
          getResources('stock/low-stock'),
          getResources('stock/out-of-stock'),
          getResources('stock/movements-last-6-months'),
        ]);

        setData({
          summary: summaryRes.data,
          categories: categoryRes.data,
          lowStock: lowStockRes.data,
          outOfStock: outOfStockRes.data,
          movements: movementsRes.data,
        });
      } catch (error) {
        console.error('Erreur chargement données stock:', error);
        // Fallback avec tes données statiques
        // setData({
        //   summary: {
        //     totalValue: 456780,
        //     totalProducts: 245,
        //     lowStock: 18,
        //     outOfStock: 5,
        //     overstock: 12,
        //   },
        //   categories: [
        //     { category: 'Informatique', value: 185600, products: 78, percentage: 40.6 },
        //     { category: 'Accessoires', value: 98450, products: 95, percentage: 21.6 },
        //     { category: 'Bureautique', value: 76890, products: 42, percentage: 16.8 },
        //     { category: 'Audio', value: 54320, products: 18, percentage: 11.9 },
        //     { category: 'Stockage', value: 41520, products: 12, percentage: 9.1 },
        //   ],
        //   lowStock: [
        //     { sku: 'SKU-001', name: 'Ordinateur Portable Dell XPS 15', current: 3, min: 10, status: 'Critique' },
        //     { sku: 'SKU-015', name: 'Écran Samsung 27" 4K', current: 5, min: 15, status: 'Faible' },
        //     { sku: 'SKU-023', name: 'Clavier Mécanique Logitech', current: 8, min: 20, status: 'Faible' },
        //     { sku: 'SKU-034', name: 'Souris Sans Fil Microsoft', current: 12, min: 25, status: 'Faible' },
        //     { sku: 'SKU-042', name: 'Webcam HD Logitech', current: 6, min: 15, status: 'Faible' },
        //   ],
        //   outOfStock: [
        //     { sku: 'SKU-056', name: 'Tablette Samsung Galaxy Tab S8', lastStock: '2024-01-10' },
        //     { sku: 'SKU-067', name: 'Casque Audio Sony WH-1000XM5', lastStock: '2024-01-12' },
        //     { sku: 'SKU-078', name: 'Disque Dur Externe 2TB', lastStock: '2024-01-13' },
        //     { sku: 'SKU-089', name: 'Routeur WiFi 6 TP-Link', lastStock: '2024-01-14' },
        //     { sku: 'SKU-095', name: 'Câble HDMI 2m Premium', lastStock: '2024-01-15' },
        //   ],
        //   movements: [
        //     { month: 'Jan', entries: 1250, exits: 1180 },
        //     { month: 'Fév', entries: 1340, exits: 1290 },
        //     { month: 'Mar', entries: 1280, exits: 1245 },
        //     { month: 'Avr', entries: 1420, exits: 1380 },
        //     { month: 'Mai', entries: 1510, exits: 1450 },
        //     { month: 'Juin', entries: 1580, exits: 1520 },
        //   ],
        // });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const maxMovement = data?.movements
    ? Math.max(...data.movements.flatMap((m: any) => [m.entries, m.exits]))
    : 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Critique': return 'bg-red-100 text-red-800';
      case 'Faible': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Export Excel
  const exportToExcel = () => {
    if (!data) return;

    const rows = [
      ['RAPPORT DE GESTION DES STOCKS'],
      [`Généré le ${new Date().toLocaleDateString('fr-FR')}`],
      [],
      ['STATISTIQUES GÉNÉRALES'],
      ['Valeur Totale du Stock', formatGNF(data.summary.totalValue)],
      ['Nombre Total de Produits', data.summary.totalProducts],
      ['Produits en Stock Faible', data.summary.lowStock],
      ['Ruptures de Stock', data.summary.outOfStock],
      ['Produits en Surstock', data.summary.overstock],
      [],
      ['RÉPARTITION PAR CATÉGORIE'],
      ['Catégorie', 'Valeur Stock', 'Nombre Produits', 'Pourcentage'],
      ...data.categories.map((c: any) => [
        c.category,
        formatGNF(c.value),
        c.products,
        `${c.percentage}%`,
      ]),
      [],
      ['ALERTES STOCK FAIBLE'],
      ['SKU', 'Produit', 'Stock Actuel', 'Stock Min', 'Statut'],
      ...data.lowStock.map((p: any) => [p.sku, p.name, p.current, p.min, p.status]),
      [],
      ['RUPTURES DE STOCK'],
      ['SKU', 'Produit', 'Dernier Stock'],
      ...data.outOfStock.map((p: any) => [p.sku, p.name, p.lastStock]),
      [],
      ['MOUVEMENTS DE STOCK (6 DERNIERS MOIS)'],
      ['Mois', 'Entrées', 'Sorties', 'Solde Net'],
      ...data.movements.map((m: any) => [
        m.month,
        m.entries,
        m.exits,
        m.entries - m.exits,
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stocks');

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, `rapport_stocks_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  // Export PDF
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    const canvas = await html2canvas(dashboardRef.current, {
      scale: 2,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    pdf.save(`rapport_stocks_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-lg text-gray-500">Chargement du tableau de bord des stocks...</p>
      </div>
    );
  }

  if (!data) {
    return <div className="text-red-600">Erreur de chargement des données.</div>;
  }

  return (
    <div className="space-y-6" ref={dashboardRef}>
      {/* Header + Boutons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tableau de Bord des Stocks</h2>
          <p className="text-sm text-gray-600 mt-1">Situation au {new Date().toLocaleDateString('fr-FR')}</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition"
          >
            <i className="ri-file-pdf-line"></i>
            Export PDF
          </button>
          <button
            onClick={exportToExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition"
          >
            <i className="ri-file-excel-line"></i>
            Export Excel
          </button>
        </div>
      </div>

      {/* Cartes statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
            <i className="ri-money-dollar-circle-line text-2xl"></i>
          </div>
          <p className="text-sm opacity-90 mb-1">Valeur Totale</p>
          <p className="text-2xl font-bold">{formatGNF(data.summary.totalValue)}</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
            <i className="ri-archive-line text-2xl"></i>
          </div>
          <p className="text-sm opacity-90 mb-1">Total Produits</p>
          <p className="text-2xl font-bold">{data.summary.totalProducts}</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <p className="text-sm opacity-90 mb-1">Stock Faible</p>
          <p className="text-2xl font-bold">{data.summary.lowStock}</p>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-6 text-white">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
            <i className="ri-close-circle-line text-2xl"></i>
          </div>
          <p className="text-sm opacity-90 mb-1">Ruptures</p>
          <p className="text-2xl font-bold">{data.summary.outOfStock}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
            <i className="ri-stack-line text-2xl"></i>
          </div>
          <p className="text-sm opacity-90 mb-1">Surstock</p>
          <p className="text-2xl font-bold">{data.summary.overstock}</p>
        </div>
      </div>

      {/* Répartition par catégorie */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Répartition par Catégorie</h3>
        <div className="space-y-4">
          {data.categories.map((item: any, i: number) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-medium text-gray-900">{item.category}</span>
                  <span className="text-xs text-gray-500 ml-2">({item.products} produits)</span>
                </div>
                <span className="text-sm font-semibold">
                  {formatGNF(item.value)} ({item.percentage}%)
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                  style={{ width: `${item.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alertes & Ruptures */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-orange-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-error-warning-line text-2xl text-orange-600"></i>
            <h3 className="text-lg font-semibold text-gray-900">Alertes Stock Faible</h3>
          </div>
          <div className="space-y-3">
            {data.lowStock.map((p: any, i: number) => (
              <div key={i} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.sku}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(p.status)}`}>
                    {p.status}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Stock actuel: <strong className="text-orange-600">{p.current}</strong></span>
                  <span>Minimum: <strong>{p.min}</strong></span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-close-circle-line text-2xl text-red-600"></i>
            <h3 className="text-lg font-semibold text-gray-900">Ruptures de Stock</h3>
          </div>
          <div className="space-y-3">
            {data.outOfStock.map((p: any, i: number) => (
              <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.sku}</p>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    Rupture
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  <i className="ri-calendar-line mr-1"></i>
                  Dernier stock: {p.lastStock}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mouvements de stock */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Mouvements de Stock (6 derniers mois)</h3>
        <div className="space-y-6">
          {data.movements.map((m: any, i: number) => {
            const net = m.entries - m.exits;
            return (
              <div key={i} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 w-16">{m.month}</span>
                  <div className="flex-1 flex items-center gap-6">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-green-600">Entrées</span>
                        <span className="font-medium text-green-700">{m.entries} unités</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full"
                          style={{ width: `${(m.entries / maxMovement) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-blue-600">Sorties</span>
                        <span className="font-medium text-blue-700">{m.exits} unités</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                          style={{ width: `${(m.exits / maxMovement) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="w-24 text-right">
                    <span className={`text-sm font-bold ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {net >= 0 ? '+' : ''}{net}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}